import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { ModalOverlay } from "../ModalOverlay";
import {
  REMOVAL_REASON_OPTIONS,
  type Partner,
  type RemovalReason,
} from "./labPartnerTypes";

type LabPartnerRemoveDialogProps = {
  open: boolean;
  partner: Partner | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    removalReason?: RemovalReason;
    removalNote?: string;
  }) => void;
};

export default function LabPartnerRemoveDialog({
  open,
  partner,
  submitting,
  onClose,
  onConfirm,
}: LabPartnerRemoveDialogProps) {
  const [reason, setReason] = useState<RemovalReason | "">("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setNote("");
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onClose();
    }
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, submitting]);

  if (!open || !partner) return null;

  const isPersonal = !!partner.isShadow;

  return (
    <ModalOverlay
      className="fixed inset-0 z-[210] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-partner-remove-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        className="relative my-8 w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-700 sm:px-5">
          <div>
            <h2
              id="lab-partner-remove-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              Remove partner lab
            </h2>
            <p className="mt-1 text-sm text-slate-500">{partner.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>

        <div className="space-y-4 px-4 py-4 text-sm sm:px-5">
          <p className="text-slate-600 dark:text-slate-300">
            {isPersonal
              ? "Past orders linked to this lab stay in your order history."
              : "This removes the lab from your partner list. You can add it again from the directory. Past orders remain in Lab Orders."}
          </p>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
              Why are you removing this lab? (optional)
            </label>
            <select
              value={reason}
              onChange={(e) =>
                setReason(e.target.value as RemovalReason | "")
              }
              className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
            >
              <option value="">Select a reason</option>
              {REMOVAL_REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {reason === "now_registered" && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Tip: add the registered lab from the directory first. Past orders
              stay linked to this personal entry in history.
            </p>
          )}
          {reason === "wrong_details" && isPersonal && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              You can Edit this personal lab instead of removing it.
            </p>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
              Notes (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={
                reason === "other"
                  ? "Please describe why you are removing this lab"
                  : "Additional notes"
              }
              className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2"
            />
          </div>
        </div>

        <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() =>
              onConfirm({
                removalReason: reason || undefined,
                removalNote: note.trim() || undefined,
              })
            }
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? "Removing…" : "Remove"}
          </button>
        </footer>
      </div>
    </ModalOverlay>
  );
}
